/**
 * Server-side PDF rendering surface.
 *
 * This page renders ONLY the deterministic <PrintDocument> component with NO
 * navigation, dialogs, drawers, or side elements. It's the URL Playwright
 * navigates to when the backend's /api/reports/{id}/pdf endpoint is hit.
 *
 * Two access modes:
 *   /pdf-render/:reportId?auth=<jwt>          → authenticated (patient/lab_admin/super_admin)
 *   /pdf-render/public/:slug/:token           → public share link (no auth)
 *
 * A signal element `<div data-testid="print-ready">` is rendered once ALL data
 * (report, lab, history, images) has finished loading — Playwright polls for
 * this before triggering PDF export.
 */
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { PrintDocument } from "./pdf/PrintDocument";
import { computeHealthScore, scoreBand } from "@/lib/health";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ---------- Authenticated variant ---------- */
export function PrintOnlyReport() {
  const { reportId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("auth") || "";
  return <PrintCanvas fetchFn={() => fetchAuthenticated(reportId, token)} />;
}

/* ---------- Public share-link variant ---------- */
export function PrintOnlyPublic() {
  const { slug, token } = useParams();
  return <PrintCanvas fetchFn={() => fetchPublic(slug, token)} />;
}

/* ---------- Shared canvas ---------- */
function PrintCanvas({ fetchFn }) {
  const [state, setState] = useState({ report: null, lab: null, history: [], error: null });
  useEffect(() => {
    fetchFn()
      .then((data) => setState({ ...data, error: null }))
      .catch((e) => setState({ report: null, lab: null, history: [], error: e?.message || "Failed to load" }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { report, lab, history, error } = state;
  if (error) return <div style={{ padding: 20, fontFamily: "sans-serif", color: "#b91c1c" }}>Error: {error}</div>;
  if (!report) return <div style={{ padding: 20, fontFamily: "sans-serif", color: "#666" }}>Loading report…</div>;

  const smart = report.smart_report || {};
  const score = computeHealthScore(smart) || report.health_score;
  const band = scoreBand(score) || report.health_band;

  return (
    <>
      {/* Global CSS reset — kill any interfering styles and position PrintDocument visibly */}
      <style>{`
        body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
        html, body { width: auto !important; }
        /* Bring PrintDocument into normal document flow so Chromium's page.pdf() captures every page */
        .pdf-doc { position: static !important; left: 0 !important; top: 0 !important; }
        /* Do NOT hide .pdf-measurer here — the PrintDocument relies on measuring its blocks
           via getBoundingClientRect to paginate. display:none breaks that. The measurer
           is already visually hidden via visibility:hidden and left:-99999px in the
           component's own inline stylesheet, which is enough for the PDF snapshot. */
        /* Per-page break rules — force Chromium's page.pdf() to break at each .pdf-page.
           PrintDocument only sets these inside @media print, but Playwright's page.pdf()
           uses the print media context, so those rules already apply. This is a fail-safe. */
        .pdf-doc .pdf-page { break-after: page; page-break-after: always; break-inside: avoid; }
        .pdf-doc .pdf-page:last-child { break-after: auto; page-break-after: auto; }
        /* Ensure the measurer never leaks onto the printed page even if positioned near 0. */
        @media print { .pdf-doc .pdf-measurer { display: none !important; } }
      `}</style>
      <div data-testid="print-only-root">
        <PrintDocument report={report} lab={lab} smart={smart} healthScore={score} band={band} history={history} />
        {/* Playwright signal — Chromium waits for this before invoking page.pdf() */}
        <div
          data-testid="print-ready"
          style={{ position: "fixed", bottom: -9999, left: -9999, width: 1, height: 1, opacity: 0 }}
        />
      </div>
    </>
  );
}

/* ---------- Data fetchers ---------- */
async function fetchAuthenticated(reportId, token) {
  const h = token ? { Authorization: `Bearer ${token}` } : {};
  const [r, h1] = await Promise.all([
    axios.get(`${API}/reports/${reportId}`, { headers: h }),
    axios.get(`${API}/reports/${reportId}/history`, { headers: h }).catch(() => ({ data: [] })),
  ]);
  const report = r.data;
  let lab = null;
  if (report?.lab_id) {
    try { lab = (await axios.get(`${API}/labs/${report.lab_id}`, { headers: h })).data; } catch { /* noop */ }
  }
  return { report, lab, history: h1.data || [] };
}

async function fetchPublic(slug, token) {
  const r = await axios.get(`${API}/public/labs/${slug}/reports/${token}`);
  return { report: r.data.report, lab: r.data.lab, history: r.data.history || [] };
}
